import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import BASE_URL from "../api";
import { AppDispatch, RootState } from "../../../store/store";
import { AuthResponse, AuthState, UpdateProfileRequest, User } from "../types/auth";

export interface LoginRequest {
  email: string;
  password: string;
}

// types.ts
export interface RegisterPayload {
  surname: string;
  first_name: string;
  gender: string;
  institution: string;
  cat_of_school: string;
  matric_no: string;
  address: string;
  email: string;
  phone: string;
  password: string;
  password_confirmation: string;
  profile_picture: File | null;
}

const storedUser = localStorage.getItem("user");
const storedToken = localStorage.getItem("access_token");

const initialState: AuthState & { 
  success: string | null; 
  isAuthenticated: boolean
 justLoggedIn: boolean;
} = {
 user: storedUser ? JSON.parse(storedUser) : null,
 // access_token: localStorage.getItem("access_token") || null,
  access_token: storedToken,
 loading: false,
  error: null,
  success: null,
   isAuthenticated: !!storedToken, 
 // isAuthenticated: !!localStorage.getItem("access_token"),
justLoggedIn: false,
};

/* =======================
   API HELPER
======================= */

async function api<T>(
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  token?: string,
  body?: unknown
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json();

  if (!res.ok) {
     throw data; // ⬅️ THROW THE FULL RESPONSE
   // throw new Error(data?.message || "Request failed");
  }

  return data as T;
}

/* =======================
   SLICE
======================= */

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    start(state) {
      state.loading = true;
      state.error = null;
      state.success = null;
    },

    fail(state, action: PayloadAction<any>) {
      state.loading = false;
      state.error = action.payload;
    },
    registerSuccess(
  state,
  action: PayloadAction<{ user: User; message: string }>
) {
  state.loading = false;

  // ❌ DO NOT authenticate
  state.user = null;
  state.access_token = null;
  state.isAuthenticated = false;
  state.justLoggedIn = false;

  state.success = action.payload.message;
  state.error = null;

  // ❌ NEVER persist on register
  localStorage.removeItem("access_token");
  localStorage.removeItem("user");
}
,

authSuccess(
  state,
  action: PayloadAction<{
    user: User;
    access_token: string;
    message: string;
  }>
) {
  state.loading = false;
  state.user = action.payload.user;
  state.access_token = action.payload.access_token;
  state.isAuthenticated = true;
   state.justLoggedIn = true;
 // state.isAuthenticated = !!action.payload.access_token;
  state.success = action.payload.message;
  state.error = null;

  // ✅ persist for refresh
  localStorage.setItem("access_token", action.payload.access_token);
  localStorage.setItem("user", JSON.stringify(action.payload.user));

  //  if (action.payload.access_token) {
  //   localStorage.setItem("access_token", action.payload.access_token);
  //   localStorage.setItem("user", JSON.stringify(action.payload.user));
  // }
}
,
  setUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
      state.isAuthenticated = true;
    },


    updateUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
      state.loading = false;
    },

    clearAuthFeedback(state) {
      state.error = null;
      state.success = null;
       state.justLoggedIn = false;
    },

    logout(state) {
  state.user = null;
      state.access_token = null;
      state.isAuthenticated = false;
       state.justLoggedIn = false;
      state.loading = false;
      state.error = null;
      state.success = null;

       localStorage.removeItem("access_token");
  localStorage.removeItem("user");
  localStorage.clear();
    },
  },
});

export const {
  start,
  fail,
  registerSuccess,
  authSuccess,
  setUser,
  updateUser,
  clearAuthFeedback,
  logout,
} = authSlice.actions;

export default authSlice.reducer;

/* =======================
   THUNKS
======================= */
export const register =
  (body: RegisterPayload) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (getState().auth.loading) return;

    dispatch(start());

    try {
      const formData = new FormData();

      Object.entries(body).forEach(([key, value]) => {
        if (value !== null) {
          formData.append(key, value as any);
        }
      });

      const res = await fetch(`${BASE_URL}/auth/register`, {
        method: "POST",
        body: formData, // ✅ NO JSON
      });

      const data = await res.json();

      if (!res.ok) throw data;

      dispatch(
        registerSuccess({
          user: data.user,
          message: "Account created successfully 🎉",
        })
      );
    } catch (err: any) {
      if (getState().auth.user) return;
      dispatch(fail(err));
    }
  };

export const login =
  (body: LoginRequest) =>
  async (dispatch: AppDispatch) => {
    dispatch(start());
    try {
      const data = await api<AuthResponse>("/auth/login", "POST", undefined, body);

      //console.log("LOGIN RESPONSE:", data); 
      //localStorage.setItem("access_token", data.access_token);

      dispatch(
        authSuccess({
          user: data.user,
          access_token: data.access_token,
          message: "Login successful ✅",
        })
      );
    } catch (err: any) {
      dispatch(fail(err));
    }
  };

export const fetchMe =
  () =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(start());
    try {
      const token = getState().auth.access_token || localStorage.getItem("access_token");
      if (!token) throw new Error("Token missing");

      const data = await api<{ user: User }>("/api/user/me", "GET", token);
      dispatch(setUser(data.user));
    } catch (err: any) {
      dispatch(fail(err.message));
      dispatch(logout());
      localStorage.removeItem("access_token");
    }
  };

  export const updateProfile =
  (body: UpdateProfileRequest) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(start());

    try {
      const token = getState().auth.access_token;
      if (!token) throw new Error("Unauthorized");

      const res = await api<{ user: User; message: string }>(
        "/user/profile",
        "PUT",
        token,
        body
      );

      dispatch(updateUser(res.user));

      // persist update
      localStorage.setItem("user", JSON.stringify(res.user));
    } catch (err: any) {
      dispatch(fail(err));
    }
  };


export const logoutUser =
  () => async (dispatch: AppDispatch) => {
    localStorage.removeItem("access_token");
    dispatch(logout());
  };


  export type { AuthState };